/**
 * @fileoverview Acompanhamento da operação da conexão: progresso (processadas
 * + preservadas sobre o total da confirmação), contadores, cancelamento e,
 * se falhou, retomada.
 *
 * Leitor de tela: só a linha de status é anunciada (o resto muda a cada
 * segundo); a barra tem nome e, na fila, não informa valor.
 */

import React from "react";

import useStyles from "./styles";
import { Stat, StatusBadge } from "./parts";
import { filtersSummary, fmtDateTime, fmtNumber, isActiveStatus, messageForCode, t } from "./format";

const ProgressPanel = ({ op, onCancel, onResume, onNew }) => {
	const classes = useStyles();
	const active = isActiveStatus(op.status);
	const total = Number(op.totalCount) || 0;
	const done = Math.min(total, (Number(op.processedCount) || 0) + (Number(op.skippedCount) || 0));
	const percent = Math.max(0, Math.min(100, Number(op.percent) || 0));
	const isResolve = op.operation === "resolve_tickets";
	const isTickets = op.operation !== "delete_contacts";
	const queued = op.status === "pending";

	return (
		<div className={classes.progress}>
			<div className={classes.progressHead}>
				<div style={{ minWidth: 0 }}>
					<h3 className={classes.progressTitle}>{t(`progress.heading.${op.operation}`)}</h3>
					<div className={classes.progressFilters}>{filtersSummary(op.operation, op.filters)}</div>
				</div>
				<div aria-live="polite">
					<StatusBadge status={op.status} />
				</div>
			</div>

			<div className={classes.progressNumbers}>
				{t("progress.count", { done: fmtNumber(done), total: fmtNumber(total) })}
				<span className={classes.progressPct}>{percent}%</span>
			</div>
			<div
				className={classes.bar}
				role="progressbar"
				aria-label={t(isResolve ? "progress.resolve.barLabel" : "progress.barLabel")}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={queued ? undefined : percent}
			>
				{queued ? (
					<div className={classes.barIndeterminate} />
				) : (
					<div className={classes.barFill} style={{ width: `${percent}%` }} />
				)}
			</div>

			<div className={classes.progressStats}>
				<Stat
					value={op.processedCount}
					label={
						isResolve
							? t("progress.resolve.processed")
							: isTickets
							? t("progress.deletedTickets")
							: t("progress.deletedContacts")
					}
				/>
				<Stat
					value={op.skippedCount}
					label={isTickets ? t("progress.skippedTickets") : t("progress.skippedContacts")}
				/>
				{!isResolve && <Stat value={op.messagesDeleted} label={t("progress.messages")} />}
				{!isResolve && <Stat value={op.mediaFiles} label={t("progress.media")} />}
			</div>

			{queued && <div className={classes.progressMeta}>{t("progress.queued")}</div>}
			{op.status === "cancel_requested" && (
				<div className={classes.progressMeta}>
					{t(isResolve ? "progress.resolve.canceling" : "progress.canceling")}
				</div>
			)}
			{!active && Number(op.skippedCount) > 0 && (
				<div className={classes.progressMeta}>
					{isTickets ? t("progress.skippedNoteTickets") : t("progress.skippedNoteContacts")}
				</div>
			)}
			{op.status === "failed" && (
				<div className={classes.errorBox} role="alert">
					{op.errorCode && op.errorCode !== "ERR_ADMIN_OPERATION_FAILED"
						? messageForCode(op.errorCode)
						: t("progress.failedHint")}
					{op.lastError ? <div style={{ opacity: 0.8, marginTop: 4 }}>{op.lastError}</div> : null}
				</div>
			)}

			<div className={classes.progressMeta}>
				{t("progress.startedBy", { user: op.userName || "—", date: fmtDateTime(op.createdAt) })}
				{op.resumedAt
					? ` · ${t("progress.resumedBy", { user: op.resumedByName || "—", date: fmtDateTime(op.resumedAt) })}`
					: ""}
				{op.cancelRequestedAt
					? ` · ${t("progress.cancelRequestedBy", {
							user: op.cancelRequestedByName || "—",
							date: fmtDateTime(op.cancelRequestedAt),
					  })}`
					: ""}
				{op.finishedAt ? ` · ${t("progress.finishedAt", { date: fmtDateTime(op.finishedAt) })}` : ""}
			</div>

			<div className={classes.progressActions}>
				{active && op.status !== "cancel_requested" && (
					<button type="button" className={classes.btnGhostDanger} onClick={() => onCancel(op)}>
						{t("progress.cancel")}
					</button>
				)}
				{op.status === "failed" && (
					<button
						type="button"
						className={isResolve ? classes.btnInk : classes.btnDanger}
						onClick={() => onResume(op)}
					>
						{t("progress.resume")}
					</button>
				)}
				{!active && (
					<button type="button" className={classes.btnGhost} onClick={onNew}>
						{t(isResolve ? "progress.resolve.newCleanup" : "progress.newCleanup")}
					</button>
				)}
			</div>
		</div>
	);
};

export default ProgressPanel;
